import React, { Component } from 'react';
import { FormattedNumber } from 'react-intl';
import ReactSlider from 'react-slider';

import { Icon } from '@99designs/common/components/Icon';
import { Tooltip } from '@99designs/common/components/Tooltip';
import { FormattedMessage } from '@99designs/common/i18n';
import { Currency, CurrencySymbol, toSubUnit, toUnit } from 'common/utils/currency';
import getBudgetStepsForCurrency from './getBudgetStepsForCurrency';

export const enum BudgetType {
    TEXT_BOX_FIELD = 'textBoxField',
    SLIDER_FIELD = 'sliderField',
}

export interface Props {
    budget: number | null;
    onSliderChange: (budget: number, inputType: string) => void;
    currency: Currency;
}

export function getClosestIndex(stepMapping: number[], maxValue: number) {
    let current = 0;
    let currentStep = 0;
    let minimum = 0;

    for (let i = 0; i < stepMapping.length; i++) {
        const step = stepMapping[i];

        current = Math.abs(step - maxValue);
        currentStep = stepMapping.indexOf(step);

        if (i === 0) {
            minimum = current;
        }

        if (current > minimum) {
            currentStep = currentStep - 1;
            break;
        }

        minimum = current;
    }

    return currentStep;
}

interface FormattedCurrencyProps {
    value: number;
    currency: Currency;
}

export const FormattedCurrency: React.SFC<FormattedCurrencyProps> = (
    props: FormattedCurrencyProps
) => (
    <FormattedNumber
        value={props.value}
        style="currency"
        currency={props.currency}
        minimumFractionDigits={0}
    />
);

export default class BudgetSlider extends Component<Props> {
    stepMapping: number[];

    constructor(props: Props) {
        super(props);
        this.stepMapping = getBudgetStepsForCurrency(props.currency).map(value =>
            toSubUnit(value, props.currency)
        );
    }

    onChange = (value: number, inputType: string) => {
        this.props.onSliderChange(
            value < this.stepMapping.length ? this.stepMapping[value] : 0,
            inputType
        );
    };

    renderLabel() {
        const maxBudget = toUnit(this.props.budget || 0, this.props.currency);

        if (!maxBudget) {
            return __('Any price');
        }

        if (maxBudget) {
            return (
                <FormattedMessage
                    message={__('Up to {price}')}
                    values={{
                        price: (
                            <FormattedCurrency value={maxBudget} currency={this.props.currency} />
                        ),
                    }}
                />
            );
        }

        throw new Error('unreachable');
    }

    getSliderValue() {
        return this.props.budget
            ? getClosestIndex(this.stepMapping, this.props.budget)
            : this.stepMapping.length;
    }

    shouldShowPlaceholder() {
        return this.props.budget === 0 || this.props.budget === null;
    }

    render() {
        const sliderClasses = 'slider slider--budget slider--initialised';
        const { currency } = this.props;

        return (
            <div className="budget-slider">
                <div className="budget-slider__heading">{this.renderLabel()}</div>
                <div className="budget-slider__input">
                    <div className="budget-input">
                        <div className="budget-input__input">
                            <div className="budget-input__input__currency">
                                {CurrencySymbol[currency]}
                            </div>
                            <input
                                type="number"
                                min={0}
                                max={Number.MAX_SAFE_INTEGER}
                                className="input-text input-width--x-short"
                                value={
                                    this.shouldShowPlaceholder()
                                        ? ''
                                        : toUnit(this.props.budget || 0, currency)
                                }
                                placeholder={__('Enter amount')}
                                onChange={({ target }) => {
                                    this.props.onSliderChange(
                                        toSubUnit(parseInt(target.value), currency),
                                        BudgetType.TEXT_BOX_FIELD
                                    );
                                }}
                            />
                        </div>
                    </div>
                    <div className="budget-input__tooltip">
                        {__('5% platform fee will apply')}
                        <Tooltip
                            position={'left-top'}
                            tooltip={__(
                                'This fee is calculated from the project price, it includes the cost of support and all processing fees.'
                            )}
                        >
                            <span className="stock-example-trigger">
                                <Icon type={'question-mark-1'} variations={['offset-left']} />
                            </span>
                        </Tooltip>
                    </div>
                </div>
                <div className="budget-slider__slider">
                    <ReactSlider
                        value={[this.getSliderValue()]}
                        max={this.stepMapping.length}
                        handleClassName="slider__handle"
                        handleActiveClassName="slider__handle--active"
                        className={sliderClasses}
                        onChange={e => this.onChange(e as number, BudgetType.SLIDER_FIELD)}
                        withBars={true}
                        orientation="horizontal"
                    />
                </div>
            </div>
        );
    }
}
